import { useState } from 'react';

export default function CTA() {
  const [formData, setFormData] = useState({
    authorName: '',
    email: '',
    bookTitle: '',
    amazonLink: '',
    goodreadsLink: '',
    genre: '',
    description: '',
    previousPromotion: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4jj71b3rief4cfeacqg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          authorName: '',
          email: '',
          bookTitle: '',
          amazonLink: '',
          goodreadsLink: '',
          genre: '',
          description: '',
          previousPromotion: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section id="request-slot" className="py-24 bg-gradient-to-b from-gray-50 to-amber-50">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-5xl font-bold text-gray-900 mb-6">Request Your Campaign Slot</h2>
          <div className="w-24 h-1 bg-amber-500 mx-auto mb-8"></div>
          <p className="text-xl text-gray-700 max-w-2xl mx-auto leading-relaxed">
            Submit your book for evaluation. We'll review your submission and reach out via email if your book is selected for our limited campaign slots.
          </p>
        </div>
        
        <div className="bg-white rounded-2xl shadow-2xl p-10 border-t-4 border-amber-500">
          <form id="request-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="authorName" className="block text-sm font-semibold text-gray-900 mb-2">
                  Author Name *
                </label>
                <input
                  type="text"
                  id="authorName"
                  name="authorName"
                  value={formData.authorName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 text-sm border-2 border-gray-300 rounded-lg focus:border-amber-500 focus:outline-none transition-colors"
                  placeholder="Your full name"
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 text-sm border-2 border-gray-300 rounded-lg focus:border-amber-500 focus:outline-none transition-colors"
                  placeholder="your@email.com"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="bookTitle" className="block text-sm font-semibold text-gray-900 mb-2">
                Book Title *
              </label>
              <input
                type="text"
                id="bookTitle"
                name="bookTitle"
                value={formData.bookTitle}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 text-sm border-2 border-gray-300 rounded-lg focus:border-amber-500 focus:outline-none transition-colors"
                placeholder="The title of your book"
              />
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="amazonLink" className="block text-sm font-semibold text-gray-900 mb-2">
                  Amazon Link *
                </label>
                <input
                  type="url"
                  id="amazonLink"
                  name="amazonLink"
                  value={formData.amazonLink}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 text-sm border-2 border-gray-300 rounded-lg focus:border-amber-500 focus:outline-none transition-colors"
                  placeholder="https://amazon.com/..."
                />
              </div>
              
              <div>
                <label htmlFor="goodreadsLink" className="block text-sm font-semibold text-gray-900 mb-2">
                  Goodreads Link
                </label>
                <input
                  type="url"
                  id="goodreadsLink"
                  name="goodreadsLink"
                  value={formData.goodreadsLink}
                  onChange={handleChange}
                  className="w-full px-4 py-3 text-sm border-2 border-gray-300 rounded-lg focus:border-amber-500 focus:outline-none transition-colors"
                  placeholder="https://goodreads.com/..."
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="genre" className="block text-sm font-semibold text-gray-900 mb-2">
                Genre *
              </label>
              <select
                id="genre"
                name="genre"
                value={formData.genre}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 pr-8 text-sm border-2 border-gray-300 rounded-lg focus:border-amber-500 focus:outline-none transition-colors cursor-pointer"
              >
                <option value="">Select a genre</option>
                <option value="Fiction">Fiction</option>
                <option value="Mystery/Thriller">Mystery/Thriller</option>
                <option value="Romance">Romance</option>
                <option value="Science Fiction">Science Fiction</option>
                <option value="Fantasy">Fantasy</option>
                <option value="Historical Fiction">Historical Fiction</option>
                <option value="Literary Fiction">Literary Fiction</option>
                <option value="Non-Fiction">Non-Fiction</option>
                <option value="Biography/Memoir">Biography/Memoir</option>
                <option value="Self-Help">Self-Help</option>
                <option value="Other">Other</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="description" className="block text-sm font-semibold text-gray-900 mb-2">
                Brief Book Description * (Max 500 characters)
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                maxLength={500}
                rows={4}
                className="w-full px-4 py-3 text-sm border-2 border-gray-300 rounded-lg focus:border-amber-500 focus:outline-none transition-colors resize-none"
                placeholder="Tell us about your book in a few sentences..."
              ></textarea>
              <div className="text-right text-sm text-gray-500 mt-1">
                {formData.description.length}/500 characters
              </div>
            </div>
            
            <div>
              <label htmlFor="previousPromotion" className="block text-sm font-semibold text-gray-900 mb-2">
                Previous Promotion Experience
              </label>
              <textarea
                id="previousPromotion"
                name="previousPromotion"
                value={formData.previousPromotion}
                onChange={handleChange}
                maxLength={500}
                rows={3}
                className="w-full px-4 py-3 text-sm border-2 border-gray-300 rounded-lg focus:border-amber-500 focus:outline-none transition-colors resize-none"
                placeholder="Have you promoted your book before? What worked or didn't work?"
              ></textarea>
            </div>
            
            {submitStatus === 'success' && (
              <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4 flex items-center gap-3">
                <i className="ri-checkbox-circle-line text-2xl text-green-600"></i>
                <p className="text-green-800 font-semibold">
                  Thank you! We've received your submission and will review it carefully. We'll contact you via email if your book is selected.
                </p>
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4 flex items-center gap-3">
                <i className="ri-error-warning-line text-2xl text-red-600"></i>
                <p className="text-red-800 font-semibold">
                  Something went wrong. Please try again or contact us directly via email.
                </p>
              </div>
            )}
            
            <button
              type="submit"
              disabled={isSubmitting}
              className="whitespace-nowrap w-full py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-white text-lg font-bold rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-3">
                  <i className="ri-loader-4-line animate-spin text-xl"></i>
                  Submitting...
                </span>
              ) : (
                'Submit for Evaluation'
              )}
            </button>
            
            <p className="text-sm text-gray-600 text-center">
              By submitting, you agree to be contacted via email regarding your book promotion campaign.
            </p>
          </form>
        </div>
        
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-3 bg-amber-100 rounded-full px-6 py-3">
            <i className="ri-time-line text-2xl text-amber-700"></i>
            <span className="text-amber-900 font-semibold">
              Limited slots available • Selective evaluation process
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
